import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Banner, Button, Modal, RichTextEditor, type RichTextDelta } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import {
  buildSaveBody,
  mapCollaboratorsForSave,
  useMacroCollaboratorsSearch,
  useSaveMacro,
} from '@/settings/services';
import { MacroCollaboratorPicker } from './macro-collaborator-picker';
import { carriesSmartParts } from '@/encounter/lib/smart-phrase';
import type {
  MacroCollaborator,
  MacroData,
} from '@/types/settings-api';

/**
 * Macro add/edit dialog — PROD-764 PR-B.
 *
 * Uses `react-quill-new` (TypeScript-friendly fork of `react-quill`)
 * which consumes/produces the same Delta JSON as Flutter's
 * `flutter_quill` 10.7.6. Confirmed Delta-format compatibility with
 * @andrew — same wire shape, different rendering engines.
 *
 * **Wire contract** (verified against captured cURLs 2026-05-16):
 *   - Add: POST /api/Doctor/CreateUpdateMacro with `id: null`
 *   - Edit: same endpoint with `id: GUID`
 *   - Archive: same endpoint with `isDeleted: true`
 *
 * **Delta JSON double-encoding** (load-bearing — see `MacroData.macroValue`
 * JSDoc for the full contract):
 *   - On open: `JSON.parse(macro.macroValue)` → Quill Delta ops
 *   - On save: `JSON.stringify(quillContents.ops)` → wire string
 *   - On open of a fresh draft (Add): use an empty ops array
 *
 * **Scope split**:
 *   - PR-B (this) — Quill editor + macroKey + isGlobal + Archive flow.
 *     Collaborators are PRESERVED on round-trip (the existing list
 *     ships unchanged in the save body) but not editable here.
 *   - PR-C — Collaborator add/remove picker (`/GetProviderAndNurseList`).
 *   - PR-D — SignalR collaboration hub.
 *   - PR-E — History view + revert flow.
 *
 * Editor toolbar mirrors Flutter's `flutter_quill` toolbar surface
 * (bold/italic/underline/strike/list/link/image). Image upload to
 * Azure blob lands with PR-C since it shares the storage flow.
 */
export interface MacroEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing macro for edit; null for add (caller seeds the empty
   *  shell so the dialog has owner/org context). */
  initial: MacroData | null;
  /** Stub for Add mode — provides ownerId/organizationId. */
  addContext?: {
    ownerId: string;
    organizationId: string;
  };
  /** providerId for invalidating the right cache slot on save. */
  providerId: string;
}

const EMPTY_DELTA_OPS = [{ insert: '\n' }];

export function MacroEditDialog({
  open,
  onOpenChange,
  initial,
  addContext,
  providerId,
}: MacroEditDialogProps) {
  const isEdit = !!initial?.id;
  // A phrase with smart pieces (`cps-piece`) or phrase metadata (`cps-phrase`) opens READ-ONLY:
  // this editor has not registered those attributes, so Quill would strip them and a save would
  // turn the phrase's pick-lists, blanks and chart links into plain text. Archive stays available
  // (it re-sends the stored value unchanged).
  const smartPhrase = isEdit && carriesSmartParts(initial?.macroValue);
  const saveMutation = useSaveMacro(providerId);
  const [confirmingArchive, setConfirmingArchive] = useState(false);

  // Local draft state. The Quill controller is managed inside the
  // ReactQuill component (controlled via `value` / `onChange`).
  const [macroKey, setMacroKey] = useState(initial?.macroKey ?? '');
  const [isGlobal, setIsGlobal] = useState(initial?.isGlobal ?? false);
  // PR-C: collaborators are editable on the dialog. The picker bubbles
  // its debounced search term up here; we feed it into the candidate-
  // search hook and pipe the resulting list back down to the picker.
  // Wire-shape verified against captured dev cURL 2026-05-18 — bare
  // array, returns `[]` when the practitioner has no eligible
  // collaborators (which is the demo org's current state).
  const [collaborators, setCollaborators] = useState<MacroCollaborator[]>(
    initial?.macroCollaborators ?? [],
  );
  const [collaboratorSearch, setCollaboratorSearch] = useState('');

  // ownerId resolution mirrors Flutter `macro_tab.dart:1646-1648`:
  // - edit existing macro → use the macro's stored ownerId
  // - add new macro → use the current practitioner (providerId)
  // providerId is always the practitioner being viewed.
  const collaboratorOwnerId = initial?.ownerId ?? providerId;
  const collaboratorProviderId = providerId;

  const { data: collaboratorCandidates = [], isFetching: candidatesLoading } =
    useMacroCollaboratorsSearch(
      collaboratorOwnerId,
      collaboratorProviderId,
      collaboratorSearch,
    );

  /**
   * Parse the wire's stringified Delta into Quill's Delta `ops`
   * array. Falls back to a single-newline empty document if the
   * stored value is unparseable (defensive — corrupt rows shouldn't
   * brick the editor). For Add mode (no initial), seeds the empty
   * shape.
   */
  const initialDeltaOps = useMemo(() => {
    if (!initial?.macroValue) return EMPTY_DELTA_OPS;
    try {
      const parsed = JSON.parse(initial.macroValue);
      return Array.isArray(parsed) ? parsed : EMPTY_DELTA_OPS;
    } catch {
      return EMPTY_DELTA_OPS;
    }
  }, [initial?.macroValue]);

  // Editor state — Quill needs a Delta object, not the raw ops array.
  // We pass `{ ops: [...] }` shape so the underlying Quill instance
  // hydrates properly on first render.
  const [editorContents, setEditorContents] =
    useState<RichTextDelta>({ ops: initialDeltaOps });

  // Editor-remount key — increments on every open transition so the
  // underlying ReactQuill component unmounts/remounts with fresh state
  // each time. Without this, a stale Quill instance in the Radix portal
  // can briefly hydrate with the previous macro's Delta before our
  // effect-driven setEditorContents fires on the next paint. Per
  // @samer PR #180 review item 4.
  const [openCount, setOpenCount] = useState(0);

  useEffect(() => {
    if (open) {
      setMacroKey(initial?.macroKey ?? '');
      setIsGlobal(initial?.isGlobal ?? false);
      setEditorContents({ ops: initialDeltaOps });
      setCollaborators(initial?.macroCollaborators ?? []);
      setConfirmingArchive(false);
      setOpenCount((n) => n + 1);
    }
  }, [open, initial, initialDeltaOps]);

  const isValid = macroKey.trim().length > 0;

  /**
   * Dirty check — avoids spurious POSTs on unmodified Edit dialogs (per
   * @samer PR #180 review item 3). Compares the in-flight draft against
   * the source row using the same stringification that we'd ship on
   * the wire. Archive path bypasses this gate (the archive flag itself
   * IS the change). For Add mode we always treat the dialog as dirty —
   * the user explicitly clicked Save to create a new row.
   */
  const isDirty = useMemo(() => {
    if (!initial) return true;
    if (macroKey.trim() !== (initial.macroKey ?? '').trim()) return true;
    if (isGlobal !== initial.isGlobal) return true;
    const currentValue = JSON.stringify(editorContents.ops ?? []);
    if (currentValue !== (initial.macroValue ?? '')) return true;
    // Collaborator list edits count as dirty. Compare the sorted set
    // of collaboratorUserIds — order is server-side; user-side reorder
    // shouldn't fire a POST. id-on-row would change after a save
    // round-trip too, so the stable comparison key is collaboratorUserId.
    const initialIds = (initial.macroCollaborators ?? [])
      .map((c) => c.collaboratorUserId)
      .sort()
      .join('|');
    const currentIds = collaborators
      .map((c) => c.collaboratorUserId)
      .sort()
      .join('|');
    if (initialIds !== currentIds) return true;
    return false;
  }, [initial, macroKey, isGlobal, editorContents, collaborators]);

  const handleSave = (asArchive: boolean) => {
    if (!asArchive && !isValid) {
      toast.error('Macro key is required.');
      return;
    }
    // Guard against spurious POSTs on an unmodified Edit dialog. Cheap
    // no-op close — preferable to round-tripping the same Delta back
    // through the model binder (PHI-adjacent record).
    if (!asArchive && !isDirty) {
      onOpenChange(false);
      return;
    }

    // Source for the trimmed body: existing macro on edit, synthesised
    // shell on add. The shell carries owner/org context plus an empty
    // collaborators list so the wire shape is complete. PR-C: the live
    // `collaborators` state overrides `source.macroCollaborators` in
    // the buildSaveBody call below — so the source's array on archive
    // is what the backend originally had, and on save is what the user
    // currently has selected.
    const source: MacroData =
      initial ??
      ({
        id: '',
        organizationId: addContext?.organizationId ?? '',
        ownerId: addContext?.ownerId ?? '',
        ownerName: '',
        ownerPhoto: null,
        macroKey: '',
        macroValue: '',
        macroValueFormatted: '',
        createdDate: '',
        createdBy: '',
        updatedDate: '',
        updatedBy: '',
        isGlobal: false,
        isDeleted: false,
        macroHistories: [],
        macroCollaborators: [] as MacroCollaborator[],
      } satisfies MacroData);

    // Read Quill content. The double-encoding contract: stringify the
    // ops array before sending. Plaintext is what we display in the
    // list — derive it from the same content for round-trip parity.
    const ops = editorContents.ops ?? [];
    const macroValue = JSON.stringify(ops);
    const macroValueFormatted = opsToPlaintext(ops);

    // Pass the live `collaborators` state through `source` — on
    // archive we keep the original list so the backend's row history
    // remains accurate. For non-archive saves we mirror Flutter
    // `macro_tab.dart:998` via the `mapCollaboratorsForSave` helper
    // (override `macroId` on each row to the current macro's id; the
    // helper also asserts the Edit-mode invariant that macroId must
    // be non-empty when isEdit is true).
    const sourceWithLiveCollaborators: MacroData = asArchive
      ? source
      : {
          ...source,
          macroCollaborators: mapCollaboratorsForSave(
            source.id ?? '',
            collaborators,
            isEdit,
          ),
        };

    const body = buildSaveBody(sourceWithLiveCollaborators, {
      macroKey: asArchive ? source.macroKey : macroKey.trim(),
      macroValue: asArchive ? source.macroValue : macroValue,
      macroValueFormatted: asArchive
        ? source.macroValueFormatted
        : macroValueFormatted,
      isGlobal: asArchive ? source.isGlobal : isGlobal,
      isDeleted: asArchive,
    });

    saveMutation.mutate(body, {
      onSuccess: () => {
        toast.success(
          asArchive
            ? 'Macro archived'
            : isEdit
              ? 'Macro updated'
              : 'Macro created',
        );
        onOpenChange(false);
      },
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Save failed'),
    });
  };

  const toolbarModules = useMemo(
    () => ({
      toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ header: [1, 2, 3, false] }],
        ['link'],
        ['clean'],
      ],
    }),
    [],
  );

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={isEdit ? 'Edit Macro' : 'Add Macro'}
      size="xl"
      footer={<><div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={() => handleSave(false)}
              disabled={!isValid || saveMutation.isPending || smartPhrase}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={saveMutation.isPending}
            >
              Cancel
            </Button>
          </div>
          {isEdit && (
            <Button
              type="button"
              variant="tertiary"
              onClick={() => {
                if (confirmingArchive) {
                  handleSave(true);
                } else {
                  setConfirmingArchive(true);
                }
              }}
              disabled={saveMutation.isPending}
              className={
                confirmingArchive
                  ? 'bg-status-error text-text-inverse hover:bg-status-error/90'
                  : 'text-status-error hover:text-status-error hover:bg-status-error/10'
              }
            >
              {confirmingArchive ? 'Confirm Archive' : 'Archive'}
            </Button>
          )}</>}
      footerAlign="between"
    >
        

        <div className="space-y-4 py-2 max-h-overlay-body overflow-y-auto scrollbar-hide">
          {smartPhrase && (
            <Banner variant="warning" title="Smart phrase" role="status" data-slot="settings-warn">
              This phrase has smart pieces; edit it in the phrase import for now.
            </Banner>
          )}
          <div>
            <FieldLabel stacked>
              Macro key <span className="text-status-error">*</span>
            </FieldLabel>
            <TextField
              value={macroKey}
              placeholder="e.g. Routine Visit Summary"
              maxLength={120}
              disabled={smartPhrase}
              onChange={(e) => setMacroKey(e.target.value)}
            />
          </div>

          <div>
            <FieldLabel stacked>Content</FieldLabel>
            <div data-slot="settings-rich-box">
              <RichTextEditor
                // Force-remount on every open transition so a stale
                // Quill instance can't bleed a previous macro's Delta
                // into a fresh Add/Edit. See `openCount` JSDoc above.
                key={`quill-${openCount}-${initial?.id ?? 'new'}`}
                className="rounded-md border border-border-hairline-strong bg-surface-card"
                aria-label="Macro content"
                // The canonical Delta (not the HTML) is what round-trips to the wire.
                value={editorContents}
                onChange={smartPhrase ? undefined : setEditorContents}
                readOnly={smartPhrase}
                toolbar={smartPhrase ? false : undefined}
                modules={toolbarModules}
              />
            </div>
            <p data-slot="settings-field-helper" className="mt-1 text-xs text-text-muted">
              Rich-text content stored as Quill Delta JSON. Same format the
              Flutter app reads/writes — content round-trips between
              platforms unchanged.
            </p>
          </div>

          <div>
            <label className="inline-flex items-center gap-2 text-sm text-text-default">
              <input
                type="checkbox"
                checked={isGlobal}
                disabled={smartPhrase}
                onChange={(e) => setIsGlobal(e.target.checked)}
                className="h-4 w-4"
              />
              Share globally across the organisation
            </label>
            <p data-slot="settings-field-helper" className="mt-1 text-xs text-text-muted">
              When off, only collaborators (managed in a follow-up PR) have
              access alongside the owner.
            </p>
          </div>

          <MacroCollaboratorPicker
            selected={collaborators}
            candidates={collaboratorCandidates}
            isLoading={candidatesLoading}
            onSearchChange={setCollaboratorSearch}
            onChange={setCollaborators}
            disabled={smartPhrase}
          />
        </div>

        
      </Modal>
  );
}

/**
 * Best-effort plaintext rendering of a Delta ops array. Mirrors what
 * Flutter sends in `macroValueFormatted` so list-preview parity holds
 * after a save. Strips formatting attributes; preserves newlines.
 * Embeds (images, etc.) become a U+FFFC object replacement character
 * — same as the Flutter wire shape.
 */
function opsToPlaintext(ops: unknown[]): string {
  return ops
    .map((op) => {
      if (typeof op !== 'object' || op === null) return '';
      const insert = (op as { insert?: unknown }).insert;
      if (typeof insert === 'string') return insert;
      // Embed (image, etc.) — Quill uses U+FFFC the same way Flutter does.
      if (insert && typeof insert === 'object') return '￼';
      return '';
    })
    .join('');
}
